import { SuggestionType } from "./suggestionTypes.js";
import { addDays } from "./timeUtils.js";

const makeId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `kiln-suggest-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function buildKilnSuggestions({ kilnSchedule = {}, capacity = {}, now = new Date() } = {}) {
  const suggestions = [];
  const conflicts = kilnSchedule.conflicts || [];
  const underutilized = kilnSchedule.underutilizedFirings || [];
  const utilizationByDay = kilnSchedule.utilizationByDay || {};

  // Over-capacity conflicts -> suggest reschedule
  conflicts
    .filter((c) => c.reason === "over_capacity")
    .forEach((c, idx) => {
      const nextDay = addDays(new Date(c.date), 1);
      suggestions.push({
        id: makeId(),
        type: SuggestionType.RESCHEDULE_FIRING,
        title: "Reschedule firing to reduce overload",
        message: `Move one firing from ${c.date} to ${nextDay.toLocaleDateString()} to avoid overloading kiln ${c.kilnId}.`,
        firingId: c.eventIds?.[0] || null,
        fromDate: c.date,
        toDate: nextDay.toISOString(),
        createdAt: now.toISOString(),
        applied: false,
      });
    });

  // Underutilized firings -> suggest merge
  for (let i = 0; i < underutilized.length; i++) {
    for (let j = i + 1; j < underutilized.length; j++) {
      const a = underutilized[i];
      const b = underutilized[j];
      if (!a || !b) continue;
      suggestions.push({
        id: makeId(),
        type: SuggestionType.RESCHEDULE_BATCH,
        title: "Merge low-load firings",
        message: `Consider merging firings ${a.firingId} and ${b.firingId} to better use kiln ${a.kilnId}.`,
        firingId: a.firingId,
        createdAt: now.toISOString(),
        applied: false,
      });
      if (suggestions.length >= 3) break;
    }
    if (suggestions.length >= 3) break;
  }

  // Idle gaps -> suggest adding firing
  (kilnSchedule.gaps || [])
    .filter((g) => g.durationHours >= 6)
    .slice(0, 2)
    .forEach((g) => {
      suggestions.push({
        id: makeId(),
        type: SuggestionType.ADD_FIRING_IN_GAP || "add_firing_in_gap",
        title: "Use kiln idle time",
        message: `Kiln ${g.kilnId} has a ${Math.round(g.durationHours)}h gap starting ${g.start.toLocaleString()}. Consider scheduling a firing.`,
        createdAt: now.toISOString(),
        applied: false,
      });
    });

  return suggestions;
}
